import { addNewRelicPageAction } from '@ddc/new-relic';
import eventShouldBeTracked from './eventShouldBeTracked';

export const SHIFT_INIT_GRACE_MS = 5000;
export const SHIFT_LOAD_TIMEOUT_MS = 15000;
const NR_SAMPLE_PERCENT = 10;

// Before sd.js initializes, window.sd is loadShift's stub and window.sd.q is an array of queued calls.
// sd.js replaces window.sd.q with a { push } object when it takes over the queue.
const isQueueStub = (win) => Array.isArray(win?.sd?.q);

const getQueuedCalls = (win) => (isQueueStub(win) ? win.sd.q.length : 0);

/**
 * Watches the sd.js script tag loadShift adds and reports a sampled wsTaggingShiftScript PageAction when Shift tracking isn't working:
 * - status 'error': the script failed to load (blocked by an ad blocker, network error, bad URL).
 *   Reported after a grace period so queuedCalls counts the Shift calls stuck behind it, or right away if the visitor leaves first.
 * - status 'timeout': the script neither loaded nor errored in time. Watching continues and one follow-up is sent,
 *   with afterTimeout: true, if the outcome is known before the visitor leaves:
 *   - status 'lateInit': sd.js loaded late and took over the queue, with loadMs
 *   - status 'error' or 'notInitialized' as above
 * - status 'notInitialized': the script loaded but window.sd is still the queue stub after a grace period
 *
 * queuedCalls is how many window.sd calls were stuck in the stub queue when it reported.
 * Nothing is reported when sd.js loads on time and takes over the queue.
 *
 * @param {HTMLScriptElement} script The sd.js script element
 * @param {Window} [win] The window object
 */
const monitorShiftScript = (script, win = window) => {
	// decide sampling once so a timeout and its follow-up are reported together
	const tracked = eventShouldBeTracked(NR_SAMPLE_PERCENT);
	const start = Date.now();
	let timedOut = false;
	let finished = false;
	let pendingError;

	const report = (status, props = {}) => {
		if (tracked) {
			addNewRelicPageAction('wsTaggingShiftScript', {
				status,
				queuedCalls: getQueuedCalls(win),
				scriptSrc: script?.src,
				...(timedOut && status !== 'timeout' && { afterTimeout: true }),
				...props,
				nrSamplePercent: NR_SAMPLE_PERCENT
			});
		}
	};

	const reportError = () => {
		clearTimeout(pendingError);
		win?.removeEventListener?.('pagehide', reportError);
		report('error');
	};

	const loadTimeout = setTimeout(() => {
		timedOut = true;
		report('timeout');
	}, SHIFT_LOAD_TIMEOUT_MS);

	script?.addEventListener('error', () => {
		if (!finished) {
			finished = true;
			clearTimeout(loadTimeout);
			pendingError = setTimeout(reportError, SHIFT_INIT_GRACE_MS);
			win?.addEventListener?.('pagehide', reportError);
		}
	});

	script?.addEventListener('load', () => {
		if (!finished) {
			finished = true;
			clearTimeout(loadTimeout);
			const loadMs = Date.now() - start;
			setTimeout(() => {
				if (isQueueStub(win)) {
					report('notInitialized');
				} else if (timedOut) {
					report('lateInit', { loadMs });
				}
			}, SHIFT_INIT_GRACE_MS);
		}
	});
};

export default monitorShiftScript;
